// @ts-strict-ignore
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnChanges, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { createSelector, Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { map, mergeMap } from 'rxjs/operators';

import { ActivatedRouteState } from 'src/app/core/services/activated-route/activated-route-state.interface';
import { AppRoutePath } from 'src/app/routing/app-route-path.enum';
import { fromCarl } from 'src/app/store/common/common.selectors';
import { selectRouteState } from 'src/app/store/router/router.selectors';
import { selectOutletId, selectSubprocess } from 'src/app/store/runtime-variables/runtime-variables.selectors';
import { hasChanged } from 'src/app/utils/functions/change-helpers';
import { URL_PATH_SUMMARY } from 'src/app/utils/global-constants';
import { Outlet } from 'src/app/utils/models/outlet.interface';
import { Subprocess } from 'src/app/utils/models/subprocess.enum';

import { selectOutlets } from '../../dealer-locator/store/dealer-locator.selectors';
import { BypassHtmlSecurityPipe } from '../../pipes/bypass-html-security/bypass-html-security.pipe';

const selectSubprocessAndOutletId = createSelector(selectSubprocess, selectOutletId, (subProcess, outletId) => ({
  subProcess,
  outletId
}));

/*
 * ℹ️ℹ️ℹ️ in the footer we have configured text which contains links (a href), that need to be highlighted.
 * for the highlighting we offer custom styles in the scss file. In order to be applied we need to change the ViewEncapsulation to None!
 *
 * ℹ️ 👉 Defined as standalone component as we are using it in the general footer _as well as in the permanent summary!_.
 */
@Component({
  selector: 'cc-footer-disclaimer',
  templateUrl: './footer-disclaimer.component.html',
  styleUrl: './footer-disclaimer.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  // eslint-disable-next-line @angular-eslint/use-component-view-encapsulation
  encapsulation: ViewEncapsulation.None,
  imports: [CommonModule, BypassHtmlSecurityPipe]
})
export class FooterDisclaimerComponent implements OnChanges {
  @Input() public whipeout: string[] = [];
  // additionally to the whipeout attribute we have this subject (which is ugly) just to trigger the disclaimer text calculation again.
  // alternatively introduce a component store to clean this code up.
  public readonly whipeoutSubject: BehaviorSubject<string[]> = new BehaviorSubject<string[]>([]);

  public readonly disclaimer$: Observable<string[]> = combineLatest([
    this.whipeoutSubject.asObservable(),
    this.store.select(selectSubprocessAndOutletId)
  ]).pipe(
    map(([, { subProcess, outletId }]) => {
      // request dealer disclaimer, if CCri
      let dealerTexts$ = of([]) as Observable<Outlet[]>;
      if (subProcess === Subprocess.CCri) {
        dealerTexts$ = this.store.select(selectOutlets);
      }
      return [dealerTexts$, outletId];
    }),
    // request default disclaimer, and if available disclaimer for config-step, currentPage and dealer
    mergeMap(([dealerTexts$, outletId]) =>
      combineLatest([
        this.store.select(
          fromCarl.selectConfig<{ id: string; text: string[] }[]>('footer.disclaimer', [
            {
              id: 'DEFAULT',
              text: []
            }
          ])
        ),
        // TODO: Fix with MBCC-2538
        // eslint-disable-next-line @ngrx/avoid-combining-selectors
        this.store.select(selectRouteState),
        (dealerTexts$ as Observable<Outlet[]>).pipe(
          map((outlets: Outlet[]) => outlets.find(outlet => outlet.outletId === outletId)),
          map((outlet: Outlet) => (outlet?.legalFooter ? [outlet.legalFooter] : []))
        )
      ])
    ),
    map(([configTexts, activeRouteState, dealerTexts]) =>
      // dealerDisclaimer are appended
      [...this.setDisclaimerText(configTexts, activeRouteState), ...dealerTexts]
    )
  );

  public constructor(private readonly store: Store) {}

  public ngOnChanges({ whipeout }: SimpleChanges): void {
    if (hasChanged(whipeout)) {
      this.whipeoutSubject.next(whipeout.currentValue as string[]);
    }
  }

  private setDisclaimerText(
    configTexts: { id: string; text: string[] }[],
    activeRouteState: ActivatedRouteState
  ): string[] {
    if (!activeRouteState) {
      // TODO: check why!
      return [];
    }

    // detect page/step to make use of page-related disclaimer
    let page: string;
    let step: string;
    if (activeRouteState.isMotorization) {
      page = AppRoutePath.Motorization;
    } else if (activeRouteState.isSummary) {
      page = URL_PATH_SUMMARY.toLowerCase();
    } else if (activeRouteState.isConfiguration) {
      page = AppRoutePath.Configuration;
      step = activeRouteState.configurationStep;
    }

    const defaultTexts = configTexts.find(text => text.id === 'DEFAULT');
    const pageTexts = configTexts.find(text => text.id === page);
    const stepTexts = configTexts.find(text => text.id === step);

    // stepDisclaimer will override pageDisclaimer will override default disclaimer
    let disclaimer = defaultTexts;
    if (!!pageTexts || !!stepTexts) {
      disclaimer = stepTexts ?? pageTexts;
    }

    // in some cases we want to whipeout the disclaimer text
    if (this.whipeout.includes(disclaimer.id)) {
      return [];
    }

    return disclaimer.text;
  }
}
